import type { ServiceItem } from '@/cms/fields/types/checkout-page'
import { Banner } from '@sunwebgroup/uds-ui/banner'
import { Button } from '@sunwebgroup/uds-ui/button'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import * as React from 'react'
import { useIntl } from '@/intl'
import { CheckoutSection } from '@/scopes/checkout/components/CheckoutSection'
import { getIconForService } from '@/scopes/checkout/components/ServiceIcon'
import { useSendAncillaryAvailable } from '@/scopes/checkout/domain/analytics/useSendAncillaryAvailable'
import { useService } from '@/scopes/checkout/domain/hooks'
import Image from '@/scopes/common/components/Image'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { ShowMore } from '../../../../common/components/ShowMore'
import { updateBookingRedeemerService } from '../../../domain/actions'
import { createAncillariesClickedEvent } from '../../../domain/analytics'
import { ServiceType } from '../../../domain/types'
import styles from './BookingRedeemerService.module.css'

export function BookingRedeemerService({ item }: { item: ServiceItem }) {
  const intl = useIntl()
  const { service, setServiceValue } = useService(ServiceType.BookingRedeem)
  const { sendEvent } = useAnalytics()
  const { componentTitle, componentDescription, popupTitle, popupDescription, popupReadMoreLabel } = item

  useSendAncillaryAvailable(service, 'selectBookingRedeemer')

  if (!service)
    return null

  const { bookings = {}, selectedValue } = service

  if (!Object.keys(bookings).length)
    return null

  const handleClickValue
  = (bookingNumber: string | null) => {
    if (!service)
      return
    setServiceValue(updateBookingRedeemerService({
      value: bookingNumber,
    })(service))

    sendEvent(
      createAncillariesClickedEvent({
        result: bookingNumber ? 'booking selected' : 'booking removed',
        type: 'booking redeemer',
        description: 'selectBookingRedeemer',
        element: 'button',
      }),
    )
  }

  return (
    <CheckoutSection
      title={componentTitle || intl.formatMessage({ id: 'checkout_steps_ancillaries_booking_redeemer_title' })}
      description={componentDescription}
      glyph={getIconForService(ServiceType.BookingRedeem)}
      moreInformationTitle={popupTitle}
      moreInformationDescription={popupDescription}
      moreInformationReadMoreLabel={popupReadMoreLabel}
    >
      <ul className={cn(styles.bookingRedeemerUsps)}>
        <li className={styles.uspItem}>
          <Icon glyph="CheckMarkSmall" className={styles.uspIcon} />
          <Typography variant="body-m-regular-tight">{intl.formatMessage({ id: 'checkout_steps_ancillaries_booking_redeemer_usp_1' })}</Typography>
        </li>
        <li className={styles.uspItem}>
          <Icon glyph="CheckMarkSmall" className={styles.uspIcon} />
          <Typography variant="body-m-regular-snug">{intl.formatMessage({ id: 'checkout_steps_ancillaries_booking_redeemer_usp_2' })}</Typography>
        </li>
        <li className={styles.uspItem}>
          <Icon glyph="CheckMarkSmall" className={styles.uspIcon} />
          <Typography variant="body-m-regular-snug">{intl.formatMessage({ id: 'checkout_steps_ancillaries_booking_redeemer_usp_3' })}</Typography>
        </li>
      </ul>
      <div className={styles.descriptionTexts}>
        { Object.keys(bookings).length > 0 && <Typography variant="body-m-bold-snug">{intl.formatMessage({ id: 'checkout_steps_ancillaries_booking_redeemer_title_on_top_of_bookings' })}</Typography> }
        { !Object.keys(bookings).length && (
          <Typography variant="body-s-regular-snug">
            {intl.formatMessage({ id: 'checkout_steps_ancillaries_booking_redeemer_text_when_no_bookings_available' })}
          </Typography>
        )}
      </div>

      <ShowMore
        openLabel={intl.formatMessage({ id: 'show_more' })}
        closeLabel={intl.formatMessage({ id: 'show_less' })}
        visibleItems={2}
      >
        { (Object.values(bookings) || []).map(booking => (
          <div
            key={booking.bookingNumber}
            className={cn(styles.bookingRedeem)}
            data-active={selectedValue === booking.bookingNumber || undefined}
          >
            <div className={styles.bookingRedeemerCard}>
              <div className={styles.imageWrapper}>
                <Image
                  src={booking.accoImageUrl}
                  alt={booking?.description}
                  srcSet={[560, 940, 1200]}
                  sizes="(min-width: 1280px) 936px, 100vw"
                  loading="lazy"
                />
              </div>
              <div className={styles.wrapper}>
                <div className={styles.bookingRedeemerCardHeader}>
                  <div className={styles.contentContainer}>
                    <Typography className={styles.accommodationName} variant="body-m-bold-flat">{booking.description}</Typography>
                    <Typography className={styles.location} variant="body-xs-regular-flat">
                      <Icon glyph="MapPin" size={16} />
                      <span>{booking.location}</span>
                    </Typography>
                  </div>
                  <div className={styles.bookingPriceDetails}>
                    <Typography className={styles.amountPaidText} variant="body-xs-regular-flat">{intl.formatMessage({ id: 'checkout_steps_ancillaries_booking_redeemer_paid_text' })}</Typography>
                    <Typography variant="body-l-bold-snug">{intl.formatCurrency(booking.paidAmount, { showSign: booking.paidAmount < 0 })}</Typography>
                  </div>
                </div>
                <div className={styles.bookingRedeemerCardContent}>
                  <div className={styles.contentContainer}>
                    <Typography className={styles.bookingRedeemContentDetails} variant="body-s-regular-flat">
                      <Icon glyph="AirplaneToRightTop" size={16} />
                      <span>{booking.date}</span>
                    </Typography>
                    <Typography className={styles.bookingRedeemContentDetails} variant="body-s-regular-flat">
                      <Icon glyph="Family" size={16} />
                      <span>{booking.composition}</span>
                    </Typography>
                    <Typography className={styles.bookingRedeemContentDetails} variant="body-s-regular-flat">
                      <Icon glyph="CheckMarkInCircle" size={16} />
                      <span>
                        {intl.formatMessage({ id: 'checkout_steps_ancillaries_booking_redeemer_booking_number' })}
                        :
                        {' '}
                        {booking.bookingNumber}
                      </span>
                    </Typography>
                  </div>
                  <Button className={styles.addButton} variant="outlinedPrimary" size="S" onClick={() => handleClickValue(booking.bookingNumber)}>
                    {intl.formatMessage({ id: 'checkout_steps_ancillaries_booking_redeemer_add_button' })}
                  </Button>
                  <Button className={styles.removeButton} variant="transparentNeutral" size="S" onClick={() => handleClickValue(null)}>
                    <Button.Icon glyph="Dismiss" />
                    {intl.formatMessage({ id: 'checkout_steps_ancillaries_booking_redeemer_remove_button' })}
                  </Button>
                </div>
              </div>
            </div>
            <div className={styles.bookingRedeemSelectedBanner}>
              <Banner
                color="informative"
                text={intl.formatMessage({ id: 'checkout_steps_ancillaries_booking_redeemer_booking_selected' })}
                size="M"
              />
            </div>
          </div>
        ))}
      </ShowMore>
      { !selectedValue && (
        <Banner
          color="warning"
          size="M"
        >
          <Banner.Text>
            {intl.formatMessage({ id: 'checkout_steps_ancillaries_booking_redeemer_no_booking_selected' })}
          </Banner.Text>
        </Banner>
      )}
    </CheckoutSection>
  )
}
